import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import type {
  LoginError,
  LoginResponse,
} from '../../../../../hooks/useHfwLogin';
import GererMesAffairesService from '../../../../../services/GererMesAffairesService';
import OauthServiceMS from '../../../../../services/OauthServiceMS';
import ProfileService from '../../../../../services/ProfileService';
import { persistData } from '../../../../../utils/localStorage';
import {
  FRONT_URL,
  GMA_HFW_COLLABORATOR_CLIENT_ID,
} from '../../../../../variables/GlobalVariables';

export default function useGmaLogin(code?: string | null) {
  const initialError: LoginError = {
    isError: false,
    details: undefined,
  };
  const [loginResponse, setLoginResponse] = useState<LoginResponse | undefined>(
    undefined
  );
  const [error, setError] = useState<LoginError>(initialError);
  const [accessToken, setAccessToken] = useState<string>('');
  const [refreshToken, setRefreshToken] = useState<string>('');
  const { mutate: mutateLogin } = useMutation({
    mutationFn: (access_token: string) =>
      OauthServiceMS.login(
        'gma',
        undefined,
        undefined,
        undefined,
        access_token
      ),
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
    },
    onError: (error) => {
      setError({ isError: true, details: error });
    },
  });

  const { mutate: getTokensByCode } = useMutation({
    mutationFn: () =>
      GererMesAffairesService.getTokensByCode(
        code as string,
        GMA_HFW_COLLABORATOR_CLIENT_ID,
        FRONT_URL
      ),
    onSuccess: (data) => {
      persistData('gma', data);
      mutateLogin(data?.access_token);
    },
    onError: (error) => {
      setError({ isError: true, details: error });
    },
  });

  useQuery({
    enabled: !!accessToken,
    queryKey: ['OauthServiceMS.getProfileToOauthUser', accessToken],
    queryFn: () => OauthServiceMS.getProfileToOauthUser(accessToken),
    onSuccess: (data) => {
      const profileIds = data.profileIds;
      if (profileIds.length === 1) {
        getCurrentUserProfile();
      } else if (profileIds.length > 1) {
        getUserHfwProfiles();
      }
    },
    onError: (error) => {
      setError({ isError: true, details: error });
    },
  });

  const { refetch: getUserHfwProfiles } = useQuery({
    enabled: false,
    queryKey: ['ProfileService.getUserHfwProfiles', accessToken],
    queryFn: () => ProfileService.getUserHfwProfiles(accessToken),
    onSuccess: (data) => {
      setError(initialError);
      setLoginResponse({
        access_token: accessToken,
        refresh_token: refreshToken,
        users: data,
      });
    },
    onError: (error) => {
      setError({ isError: true, details: error });
    },
  });

  const { refetch: getCurrentUserProfile } = useQuery({
    enabled: false,
    queryKey: ['ProfileService.getCurrentUserProfile', accessToken],
    queryFn: () => ProfileService.getCurrentUserProfile(accessToken),
    onSuccess: (data) => {
      setError(initialError);
      setLoginResponse({
        access_token: accessToken,
        refresh_token: refreshToken,
        users: [data],
      });
    },
    onError: (error) => {
      setError({ isError: true, details: error });
    },
  });

  const login = useCallback(() => {
    if (code) {
      if (loginResponse) {
        setLoginResponse(undefined);
      }
      getTokensByCode();
    }
  }, [code]);

  return { login, error, loginResponse };
}
